import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import * as Tone from 'tone';
import api from "../services/api";
import {useData} from "../context/DataContext";
import {useConsent} from "../context/ConsentContext";
import {useAuth} from "../context/AuthContext";
import {QrCode} from "../types";
import {formatDate} from "../utils/utils";

// Tipi per lo stato della verifica
type VerificationStatus = 'idle' | 'loading' | 'success' | 'error';
interface VerificationResult {
    status: VerificationStatus;
    message: string;
    details?: string;
    qrCode?: QrCode
}

/**
 * Componente per mostrare un messaggio quando la funzionalità è bloccata per mancanza di consenso.
 */
const ConsentBlockedView: FC = () => (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-gray-100 rounded-lg">
        <div className="max-w-md p-8 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Funzionalità Bloccata</h2>
            <p className="text-gray-700">
                Per utilizzare questa funzionalità, è necessario accettare i termini della nostra Privacy Policy.
            </p>
        </div>
    </div>
);

/**
 * Componente per visualizzare il risultato della verifica manuale.
 */
const ResultDisplay: FC<{ result: VerificationResult }> = ({ result }) => {
    if (result.status === 'idle') {
        return <p className="text-gray-400">In attesa di verifica...</p>;
    }
    if (result.status === 'loading') {
        return <p className="text-gray-400">Verifica in corso...</p>;
    }

    const isSuccess = result.status === 'success';
    const textColor = isSuccess ? 'text-green-400' : 'text-red-400';
    const Icon = isSuccess ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
    ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
    );

    return (
        <div className={textColor}>
            {Icon}
            <h2 className="text-3xl font-black">{result.message}</h2>

            {/* Se è un successo e ci sono i dati del QR, mostrali */}
            {isSuccess && result.qrCode && (
                <div className="text-sm text-slate-300 mt-4 space-y-1">
                    <div className="flex justify-between">
                        <span className="text-slate-400">Id:</span>
                        <span className="text-white font-medium">{result.qrCode.id}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-400">Nome:</span>
                        <span className="text-white font-medium">{result.qrCode.name}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-400">Categoria:</span>
                        <span className="text-white font-medium">{result.qrCode.category}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-400">Valido dal:</span>
                        <span className="text-white font-medium">{result.qrCode.validFrom && result.qrCode.validFrom !== "" ? formatDate(result.qrCode.validFrom) : "Non specificato"}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-400">Valido al:</span>
                        <span className="text-white font-medium">{result.qrCode.validTo && result.qrCode.validTo !== "" ? formatDate(result.qrCode.validTo) : "Non specificato"}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-400">Cliente associato:</span>
                        <span className="text-white font-medium">{result.qrCode.client || 'Nessuno'}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-400">Stato:</span>
                        <span className={`font-medium ${result.qrCode.status === 'Attivo' ? 'text-green-400' : 'text-red-400'}`}>{result.qrCode.status}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-400">Max uso per fascia:</span>
                        <span className="text-white font-medium">
                                {result.qrCode.maxUsesFasce === -1 ? 'Illimitati' : result.qrCode.maxUsesFasce}
                            </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-400">Fasce orarie:</span>
                        <span className="text-white font-medium">
                                {result.qrCode.availability && result.qrCode.availability.length > 0
                                    ? result.qrCode.availability.map(slot => `${slot.from} - ${slot.to}`).join(', ')
                                    : 'Non specificato'
                                }
                            </span>
                    </div>
                </div>
            )}

            {/* Se è un errore e ci sono dettagli, mostrali */}
            {!isSuccess && result.details && (
                <p className="text-lg text-gray-400 mt-1">{result.details}</p>
            )}
        </div>
    );
};


const ManualVerificationPage: FC = () => {
    const { hasConsented } = useConsent();
    const { user } = useAuth();
    const { clients } = useData();

    // Stato
    const [idQrInput, setIdQrInput] = useState<string>("");
    const [selectedClientId, setSelectedClientId] = useState<number>(-1);
    const [verificationResult, setVerificationResult] = useState<VerificationResult>({ status: 'idle', message: '' });
    const [allScans, setAllScans] = useState<string>("-1");
    const [todayScans, setTodayScans] = useState<string>("0");

    // Refs
    const synthSuccessRef = useRef<Tone.Synth | null>(null);
    const synthErrorRef = useRef<Tone.Synth | null>(null);
    const audioInitialized = useRef(false);
    const isPausedRef = useRef(false); // Per prevenire invii multipli

    // Funzione per recuperare le statistiche
    const checkData = useCallback(async (clientId: number) => {
        if (user && (user.client || clientId !== -1)) {
            try {
                const response = await api.get(`/api/scans/dataToday/${clientId}`);
                const resultMessage = response.data.data;
                if (typeof resultMessage === 'string' && resultMessage.includes(";")) {
                    const [today, all] = resultMessage.split(";");
                    setTodayScans(today || "0");
                    setAllScans(all || "0");
                }
            } catch (err) {
                console.error("Errore nel recupero dati:", err);
            }
        }
    }, [user]);

    // Effetto per l'inizializzazione audio e dati
    useEffect(() => {
        if (!hasConsented) return;
        synthSuccessRef.current = new Tone.Synth().toDestination();
        synthErrorRef.current = new Tone.Synth({ oscillator: { type: 'sawtooth' } }).toDestination();
        checkData(selectedClientId);
    }, [hasConsented, checkData, selectedClientId]);

    // Gestore cambio cliente
    const handleChangeClient = (value: string) => {
        const newId = Number(value);
        setSelectedClientId(newId);
        checkData(newId);
    };

    // Funzione principale di verifica
    const handleManualVerification = async () => {
        if (!idQrInput || isPausedRef.current) {
            return;
        }

        isPausedRef.current = true; // Blocca ulteriori invii
        setVerificationResult({ status: 'loading', message: '' });

        if (!audioInitialized.current) {
            await Tone.start();
            audioInitialized.current = true;
        }

        try {
            const idQr = parseInt(idQrInput, 10);
            const response = await api.post(`/api/scans/scanid/${idQr}/${selectedClientId}/-1`);
            const scanData = response.data.data;
            console.log(response)
            if (response.data.status && scanData.result && scanData.result.toLowerCase().startsWith("valido")) {
                synthSuccessRef.current?.triggerAttackRelease("C5", "8n");
                setVerificationResult({
                    status: 'success',
                    message: 'VALIDO',
                    qrCode: scanData.qrCode
                });
                checkData(selectedClientId);
            } else {
                throw new Error(scanData.result || 'ID non valido.');
            }
        } catch (err: any) {
            synthErrorRef.current?.triggerAttackRelease("E3", "8n");
            const errorMessage = err.response?.data?.message || err.message || "Errore sconosciuto.";
            setVerificationResult({ status: 'error', message: 'NON VALIDO', details: errorMessage });
        } finally {
            setIdQrInput("");
            isPausedRef.current = false;
            //setTimeout(() => {
            //    setVerificationResult({ status: 'idle', message: '' });
            //    isPausedRef.current = false;
            //}, 2500);
        }
    };

    // Gestore per invio con tasto "Invio"
    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleManualVerification();
        }
    };

    if (!hasConsented) {
        return <ConsentBlockedView />;
    }

    return (
        <div className="bg-slate-900 text-white p-4 sm:p-8 min-h-screen">
            <h1 className="text-3xl font-bold text-white mb-6 text-center sm:text-left">Verifica Manuale ID</h1>
            <div className="max-w-md mx-auto space-y-6">

                {user && !user.client && (
                    <div className="w-full">
                        <label className="block text-white mb-2">Seleziona Cliente:</label>
                        <select
                            className="w-full p-3 rounded-md bg-slate-700 text-white cursor-pointer"
                            value={selectedClientId}
                            onChange={(e) => handleChangeClient(e.target.value)}
                        >
                            <option value="-1">Tutti i clienti</option>
                            {clients.map(client => (
                                <option key={client.id} value={client.id.toString()}>
                                    {client.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div>
                    <label htmlFor="id-input" className="block text-white mb-2">Inserisci ID del QR Code:</label>
                    <div className="flex space-x-2">
                        <input
                            id="id-input"
                            type="number"
                            value={idQrInput}
                            onChange={(e) => setIdQrInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Es. 1234"
                            className="flex-grow p-3 bg-slate-700 text-white text-center text-lg rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            onClick={handleManualVerification}
                            disabled={verificationResult.status === 'loading' || isPausedRef.current}
                            className="bg-blue-600 text-white font-bold py-3 px-6 rounded-md hover:bg-blue-700 disabled:bg-slate-500 disabled:cursor-not-allowed transition-colors"
                        >
                            {verificationResult.status === 'loading' ? '...' : 'Verifica'}
                        </button>
                    </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg min-h-[200px] flex items-center justify-center text-center p-4">
                    <ResultDisplay result={verificationResult} />
                </div>

                {user && (user.client || selectedClientId !== -1) && allScans !== "-1" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
                            <p className="text-slate-400 text-sm">Verifiche Valide Oggi</p>
                            <p className="text-white text-3xl font-bold mt-1">{todayScans}</p>
                        </div>
                        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
                            <p className="text-slate-400 text-sm">Verifiche Valide Totali</p>
                            <p className="text-white text-3xl font-bold mt-1">{allScans}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManualVerificationPage;

